import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #visibilityResizeObserver = null;

  #visibilityFrame = 0;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  // Friends: the native bar requires a deliberate option choice, not Shopify's default.
  #confirmedOptions = new Set();
  #variantLoading = false;
  #variantRequest = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#setupVisibilityObservers();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    target?.addEventListener('click', this.#handleChoice, { signal });
    target?.addEventListener('change', this.#handleChoice, { signal });
    this.#getInitialQuantity();
    this.#updateChoiceButton();

    // Friends widget clearance handles open chat/dialogs without disabling the bar.

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#visibilityResizeObserver?.disconnect();
    cancelAnimationFrame(this.#visibilityFrame);
    this.#visibilityFrame = 0;
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Reconcile against current geometry, including scrolls that skip an entire
   * intersection and scrollable ancestors used by the desktop layout.
   */
  #setupVisibilityObservers() {
    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#scheduleVisibilityUpdate);
    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#visibilityResizeObserver = new ResizeObserver(this.#scheduleVisibilityUpdate);
    this.#visibilityResizeObserver.observe(buyButtonsBlock);
    const section = this.closest('.shopify-section');
    if (section) this.#visibilityResizeObserver.observe(section);

    const { signal } = this.#abortController;
    document.addEventListener('scroll', this.#scheduleVisibilityUpdate, { capture: true, passive: true, signal });
    document.addEventListener('load', this.#scheduleVisibilityUpdate, { capture: true, signal });
    window.addEventListener('resize', this.#scheduleVisibilityUpdate, { passive: true, signal });
    window.addEventListener('pageshow', this.#scheduleVisibilityUpdate, { signal });
    window.visualViewport?.addEventListener('resize', this.#scheduleVisibilityUpdate, { passive: true, signal });
    this.#updateStickyVisibility();
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  #scheduleVisibilityUpdate = () => {
    if (this.#visibilityFrame) return;
    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = 0;
      this.#updateStickyVisibility();
    });
  };

  #updateStickyVisibility() {
    const buyButtonsBlock = this.#getProductForm()?.closest('.buy-buttons-block');
    const rect = buyButtonsBlock?.getBoundingClientRect();
    // Keep the native bar through the footer; overlay/keyboard clearance is separate.
    const shouldStick = !!(rect && rect.width > 0 && rect.height > 0 && rect.bottom <= 0);
    if (shouldStick === this.#isStuck) return;
    if (shouldStick) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#needsChoice()) {
      this.#jumpToChoice();
      return;
    }
    if (this.#variantLoading) return;
    // Variant rendering can replace this button. Always use the current native form.
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const request = ++this.#variantRequest;
    this.#variantLoading = true;
    this.#updateChoiceButton();

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (request !== this.#variantRequest || !detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      })
      .finally(() => {
        if (request !== this.#variantRequest) return;
        this.#variantLoading = false;
        this.#updateChoiceButton();
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  #getPicker() {
    return this.closest('.shopify-section')?.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );
  }

  #choiceGroups() {
    const picker = this.#getPicker();
    if (!picker) return [];
    const groups = new Map();
    for (const input of picker.querySelectorAll('input[type="radio"], select')) {
      if (input.tagName === 'SELECT') {
        if (input.options.length > 1) groups.set(input.name, [input]);
      } else {
        const group = groups.get(input.name) ?? [];
        group.push(input);
        groups.set(input.name, group);
      }
    }
    return [...groups.entries()].filter(([, inputs]) => inputs[0].tagName === 'SELECT' || inputs.length > 1);
  }

  #needsChoice() {
    if (this.dataset.selectedVariant) return false;
    return this.#choiceGroups().some(([name]) => !this.#confirmedOptions.has(name));
  }

  #handleChoice = (event) => {
    const input = event.target;
    const picker = this.#getPicker();
    if (!(input instanceof HTMLElement) || !picker?.contains(input)) return;
    if (!input.matches('input[type="radio"], select') || input.disabled || input.getAttribute('aria-disabled') === 'true') return;
    // A select click only opens the menu; its change event confirms the choice.
    if (input.tagName === 'SELECT' && event.type !== 'change') return;
    this.#confirmedOptions.add(input.name);
    this.#updateChoiceButton();
  };

  #updateChoiceButton() {
    const needsChoice = this.#needsChoice();
    this.toggleAttribute('data-friends-needs-choice', needsChoice);
    const button = this.refs.addToCartButton;
    const label = needsChoice ? this.dataset.choiceLabel : button.dataset.cartLabel;
    const text = button.querySelector('[data-friends-button-label]');
    if (text && label) text.textContent = label;
    if (label) button.setAttribute('aria-label', label);
    button.disabled = this.#variantLoading || (!needsChoice && this.dataset.variantAvailable !== 'true');
    button.setAttribute('aria-busy', String(this.#variantLoading));
  }

  #jumpToChoice() {
    const group = this.#choiceGroups().find(([name]) => !this.#confirmedOptions.has(name));
    const inputs = group?.[1];
    const input = inputs?.find(input => input.checked && input.getAttribute('aria-disabled') !== 'true')
      ?? inputs?.find(input => !input.disabled && input.getAttribute('aria-disabled') !== 'true');
    const target = input?.closest('.variant-option') ?? this.#getPicker();
    if (!target) return;
    target.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    input?.focus({ preventScroll: true });
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
